import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable, ActivityIndicator } from '../lib/react-native-compat';
import { saveQuantitySchedule, loadFieldReportQtyHistory } from '../db/company';
import { workingDayCount } from './scheduleCalendar';
import {
    defaultWeekEnding, seedWeeklyRows, weeklyHistory, gapOf, toDateByCode,
} from './weeklyUpdateData';
import WeeklyGantt from './weeklyGantt';
import { ROW_H, HEAD_H } from './weeklyGanttData';

// Weekly Update grid (Baseline & Weekly page).
//
// The third way to keep a job's planned quantity curve current: every week the
// PM opens the grid — seeded from the current plan (or the baseline uploads
// the first time) — revises quantities, dates and curves, and saves against a
// week-ending date. Each save goes through the same saveQuantitySchedule API
// as the other two schedule pages, so it becomes the active plan the daily
// field report paces against, and the save history doubles as the week-by-week
// record.

const CURVES = [
    ['linear', 'Even'],
    ['frontEnd', 'Front-loaded'],
    ['backEnd', 'Back-loaded'],
    ['bell', 'Ramp up/down'],
];

// The qty-history API keys on a report GUID only to EXCLUDE that report's own
// rows; the nil GUID matches nothing, so it returns the whole job's history.
const NIL_REPORT = '00000000-0000-0000-0000-000000000000';

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const fmt = (v, dp = 0) => (Number.isFinite(Number(v))
    ? Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : '—');

const shortDate = (d) => (d ? moment(d).format('M/D/YY') : '—');

const card = {
    borderRadius: 10,
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    padding: 12,
    marginBottom: 12,
};

const cell = {
    fontSize: 12,
    padding: '4px 6px',
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: '#0F172A',
};

// Header and row heights are fixed, and shared with the Gantt strip beside the
// grid — the strip's whole claim is that the bar on a line belongs to the row
// on that line, and a row that grows to fit its content breaks it.
const th = {
    fontSize: 10,
    fontWeight: '700',
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    padding: '0 6px',
    height: HEAD_H,
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const td = {
    padding: '3px 6px',
    height: ROW_H,
    verticalAlign: 'middle',
    borderTop: '1px solid #F1F5F9',
};

const btn = (primary, disabled) => ({
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 14,
    paddingRight: 14,
    borderRadius: 8,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    backgroundColor: primary ? '#1D4ED8' : '#fff',
    border: `1px solid ${primary ? '#1D4ED8' : '#CBD5E1'}`,
});

const btnText = (primary) => ({ fontSize: 12, fontWeight: '700', color: primary ? '#fff' : '#334155' });

const WeeklyUpdate = ({
    buId, imports, activities, baseline, quantity, userName,
    loading, viewImportId, onViewImport, onSaved,
}) => {
    const [rows, setRows] = useState([]);
    const [seededFrom, setSeededFrom] = useState('empty');
    const [weekEnding, setWeekEnding] = useState(() => defaultWeekEnding(moment().format('YYYY-MM-DD')));
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [toDate, setToDate] = useState(null);        // {code: qty} | null while loading/unavailable

    const readOnly = viewImportId != null;
    const history = useMemo(() => weeklyHistory(imports), [imports]);
    const viewing = readOnly ? history.find((h) => h.importId === viewImportId) : null;

    // Seed / re-seed whenever the loaded schedule changes, unless the PM has
    // edits in flight — a background refresh must never clobber those.
    useEffect(() => {
        if (dirty && !readOnly) return;
        const seeded = seedWeeklyRows(quantity, baseline, activities);
        setRows(seeded.rows);
        setSeededFrom(seeded.seededFrom);
    }, [quantity, baseline, activities, dirty, readOnly]);

    // Installed-to-date reference column. Depends only on the job, so a failed
    // or not-yet-deployed call just hides the column.
    useEffect(() => {
        let alive = true;
        setToDate(null);
        if (!buId) return undefined;
        loadFieldReportQtyHistory({ buId: String(buId), reportId: NIL_REPORT, reportDate: moment().format('YYYY-MM-DD') })
            .then((res) => { if (alive && res && !res.error) setToDate(toDateByCode(res.history)); })
            .catch(() => {});
        return () => { alive = false; };
    }, [buId]);

    const patch = (i, key, value) => {
        if (readOnly) return;
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
        setDirty(true);
        setNotice('');
    };

    const removeRow = (i) => {
        if (readOnly) return;
        setRows((prev) => prev.filter((_, idx) => idx !== i));
        setDirty(true);
    };

    const save = useCallback(async () => {
        if (rows.length === 0 || !weekEnding) return;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const res = await saveQuantitySchedule({
                buId: String(buId),
                savedBy: userName || null,
                source: 'weekly',
                dataDate: weekEnding,
                notes: `Weekly update w/e ${moment(weekEnding).format('M/D/YY')}`,
                rows: rows.map((r) => ({
                    costCode: r.costCode,
                    description: r.description,
                    uom: r.uom,
                    qty: num(r.qty),
                    budgetHours: r.budgetHours,
                    startDate: r.startDate,
                    finishDate: r.finishDate,
                    dailyRate: r.dailyRate ? num(r.dailyRate) : null,
                    curve: r.curve,
                    workDays: r.workDays,
                    qtySource: r.qtySource || null,
                    activityCode: r.activityCode || null,
                })),
            });
            if (res?.success === false || res?.error) throw new Error(res.error || 'Save failed');
            setDirty(false);
            setNotice(`Saved week ending ${moment(weekEnding).format('M/D/YY')} — ${fmt(res?.codeCount)} cost codes, ${fmt(res?.periodCount)} planned days.`);
            if (onSaved) onSaved();
        } catch (e) {
            setError(e?.message || 'Could not save the weekly update.');
        } finally {
            setBusy(false);
        }
    }, [rows, weekEnding, buId, userName, onSaved]);

    const totals = useMemo(() => {
        let planned = 0;
        let gaps = 0;
        let days = 0;
        rows.forEach((r) => {
            const g = gapOf(r);
            if (g) { gaps += 1; return; }
            planned += 1;
            days += r.finishDate
                ? workingDayCount(r.startDate, r.finishDate, r.workDays)
                : Math.ceil(num(r.qty) / Math.max(num(r.dailyRate), 1));
        });
        return { planned, gaps, days };
    }, [rows]);

    const showToDate = toDate != null && Object.keys(toDate).length > 0;

    return (
        <View>
            {/* Header / controls */}
            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <View>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A' }}>Weekly Update</Text>
                        <Text style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                            Revise the plan each week — the save becomes the curve the daily field report paces against.
                        </Text>
                    </View>
                    <View style={{ flex: 1 }} />
                    {(loading || busy) && <ActivityIndicator size="small" />}
                    {!readOnly && (
                        <>
                            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 6 }}>
                                <Text style={{ fontSize: 11, color: '#64748B' }}>Week ending</Text>
                                <input
                                    type="date"
                                    value={weekEnding ?? ''}
                                    onChange={(e) => { setWeekEnding(e.target.value || null); setNotice(''); }}
                                    style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #CBD5E1', borderRadius: 6 }}
                                />
                            </View>
                            <Pressable
                                onPress={save}
                                disabled={busy || loading || rows.length === 0 || !weekEnding}
                                style={btn(true, busy || loading || rows.length === 0 || !weekEnding)}
                            >
                                <Text style={btnText(true)}>Save weekly update</Text>
                            </Pressable>
                        </>
                    )}
                </View>

                {readOnly && (
                    <View style={{
                        flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 10,
                        backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 8,
                    }}
                    >
                        <Text style={{ fontSize: 12, color: '#92400E', fontWeight: '600' }}>
                            {`Viewing ${viewing?.weekEnding ? `week ending ${shortDate(viewing.weekEnding)}` : 'a past save'}`
                              + `${viewing?.by ? ` by ${viewing.by}` : ''} — read only.`}
                        </Text>
                        <Pressable onPress={() => onViewImport(null)} style={btn(false, false)}>
                            <Text style={btnText(false)}>Back to current</Text>
                        </Pressable>
                    </View>
                )}

                {!readOnly && seededFrom === 'baseline' && rows.length > 0 && (
                    <Text style={{ fontSize: 11, color: '#64748B', marginTop: 8 }}>
                        First week — seeded from the baseline uploads, with dates bound to the schedule activities
                        by name. Check them before saving.
                    </Text>
                )}

                {error ? <Text style={{ fontSize: 12, color: '#B91C1C', marginTop: 8 }}>{error}</Text> : null}
                {notice ? <Text style={{ fontSize: 12, color: '#166534', marginTop: 8 }}>{notice}</Text> : null}
            </View>

            {/* Rows */}
            <View style={card}>
                {rows.length === 0 ? (
                    <Text style={{ fontSize: 12, color: '#64748B' }}>
                        {loading
                            ? 'Loading…'
                            : 'Nothing to plan yet — upload the schedule and productivity baseline on the Baseline tab first.'}
                    </Text>
                ) : (
                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', columnGap: 12 }}>
                        <div style={{ overflowX: 'auto', flex: '2 1 0', minWidth: 0 }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1020 }}>
                                <thead>
                                    <tr>
                                        <th style={th}>Phase</th>
                                        <th style={th}>Description</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Quantity</th>
                                        <th style={th}>UOM</th>
                                        {showToDate && <th style={{ ...th, textAlign: 'right' }}>Installed</th>}
                                        <th style={th}>Start</th>
                                        <th style={th}>Finish</th>
                                        <th style={th}>Curve</th>
                                        <th style={th}>Days/wk</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Per day</th>
                                        {!readOnly && <th style={th} />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => {
                                        const gap = gapOf(r);
                                        const days = r.finishDate ? workingDayCount(r.startDate, r.finishDate, r.workDays) : 0;
                                        const perDay = days > 0 ? num(r.qty) / days : num(r.dailyRate);
                                        const done = showToDate ? num(toDate[r.costCode]) : 0;
                                        return (
                                            <tr key={`${r.costCode}-${i}`} style={{ backgroundColor: gap ? '#FFFBEB' : 'transparent' }}>
                                                <td style={td}>
                                                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#1D4ED8' }}>{r.costCode}</Text>
                                                </td>
                                                <td style={{ ...td, maxWidth: 200 }}>
                                                    <Text numberOfLines={1} style={{ fontSize: 12, color: '#334155' }}>{r.description}</Text>
                                                </td>
                                                <td style={{ ...td, width: 110 }}>
                                                    <input
                                                        type="number"
                                                        value={r.qty ?? ''}
                                                        disabled={readOnly}
                                                        onChange={(e) => patch(i, 'qty', e.target.value)}
                                                        style={{ ...cell, textAlign: 'right' }}
                                                    />
                                                </td>
                                                <td style={{ ...td, width: 62 }}>
                                                    <input
                                                        type="text"
                                                        value={r.uom ?? ''}
                                                        disabled={readOnly}
                                                        onChange={(e) => patch(i, 'uom', e.target.value)}
                                                        style={cell}
                                                    />
                                                </td>
                                                {showToDate && (
                                                    <td style={{ ...td, textAlign: 'right', width: 90 }}>
                                                        <Text style={{ fontSize: 12, color: done ? '#334155' : '#CBD5E1' }}>
                                                            {done ? fmt(done) : '—'}
                                                        </Text>
                                                    </td>
                                                )}
                                                <td style={{ ...td, width: 132 }}>
                                                    <input
                                                        type="date"
                                                        value={r.startDate ?? ''}
                                                        disabled={readOnly}
                                                        onChange={(e) => patch(i, 'startDate', e.target.value || null)}
                                                        style={cell}
                                                    />
                                                </td>
                                                <td style={{ ...td, width: 132 }}>
                                                    <input
                                                        type="date"
                                                        value={r.finishDate ?? ''}
                                                        disabled={readOnly}
                                                        onChange={(e) => patch(i, 'finishDate', e.target.value || null)}
                                                        style={cell}
                                                    />
                                                </td>
                                                <td style={{ ...td, width: 124 }}>
                                                    <select
                                                        value={r.curve}
                                                        disabled={readOnly}
                                                        onChange={(e) => patch(i, 'curve', e.target.value)}
                                                        style={cell}
                                                    >
                                                        {CURVES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                                    </select>
                                                </td>
                                                <td style={{ ...td, width: 68 }}>
                                                    <select
                                                        value={r.workDays}
                                                        disabled={readOnly}
                                                        onChange={(e) => patch(i, 'workDays', Number(e.target.value))}
                                                        style={cell}
                                                    >
                                                        <option value={5}>5</option>
                                                        <option value={6}>6</option>
                                                        <option value={7}>7</option>
                                                    </select>
                                                </td>
                                                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <Text style={{ fontSize: 12, color: gap ? '#B45309' : '#0F172A', fontWeight: '600' }}>
                                                        {gap || `${fmt(perDay, perDay < 10 ? 1 : 0)} ${r.uom || ''}`}
                                                    </Text>
                                                    {!gap && days > 0 && (
                                                        <Text style={{ fontSize: 10, color: '#94A3B8' }}>{`${days} days`}</Text>
                                                    )}
                                                </td>
                                                {!readOnly && (
                                                    <td style={{ ...td, width: 28 }}>
                                                        <Pressable onPress={() => removeRow(i)} style={{ cursor: 'pointer' }}>
                                                            <Text style={{ fontSize: 14, color: '#94A3B8' }}>×</Text>
                                                        </Pressable>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {/* Right third — the same rows, drawn on a date axis */}
                        <View style={{ flex: '1 1 0', minWidth: 220 }}>
                            <WeeklyGantt rows={rows} toDate={toDate} weekEnding={weekEnding} />
                        </View>
                    </View>
                )}

                {rows.length > 0 && (
                    <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>
                        {`${totals.planned} of ${rows.length} phases planned across ${fmt(totals.days)} working days`
                          + `${totals.gaps ? ` · ${totals.gaps} need attention` : ''}`}
                    </Text>
                )}
            </View>

            {/* Save history */}
            <View style={card}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A' }}>Save history</Text>
                {history.length === 0 ? (
                    <Text style={{ fontSize: 12, color: '#94A3B8', marginTop: 6 }}>
                        No quantity schedule saved for this job yet.
                    </Text>
                ) : history.map((h) => (
                    <Pressable
                        key={h.importId}
                        onPress={() => onViewImport(h.active ? null : h.importId)}
                        style={{
                            flexDirection: 'row', alignItems: 'center', columnGap: 8, marginTop: 6,
                            borderTop: '1px solid #F1F5F9', paddingTop: 6, cursor: 'pointer',
                            opacity: viewImportId != null && viewImportId !== h.importId ? 0.55 : 1,
                        }}
                    >
                        <Text style={{ fontSize: 12, fontWeight: '600', color: h.isWeekly ? '#0F172A' : '#64748B', width: 170 }}>
                            {h.isWeekly
                                ? `Week ending ${shortDate(h.weekEnding)}`
                                : 'Saved from the Baseline view'}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#64748B', flex: 1 }}>
                            {[
                                `${fmt(h.rows)} rows`,
                                h.by,
                                h.at ? moment.utc(h.at).format('M/D/YY h:mm A') : null,
                            ].filter(Boolean).join(' · ')}
                        </Text>
                        {h.active && (
                            <Text style={{
                                fontSize: 10, fontWeight: '700', color: '#166534', backgroundColor: '#DCFCE7',
                                borderRadius: 6, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8,
                            }}
                            >
                                ACTIVE
                            </Text>
                        )}
                        {viewImportId === h.importId && (
                            <Text style={{ fontSize: 10, fontWeight: '700', color: '#92400E' }}>VIEWING</Text>
                        )}
                    </Pressable>
                ))}
                {history.some((h) => !h.isWeekly) && (
                    <Text style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 8 }}>
                        “Saved from the Baseline view” rows came from the schedule → budget mapping — whichever
                        save is newest is the active plan.
                    </Text>
                )}
            </View>
        </View>
    );
};

export default WeeklyUpdate;
